import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useLocation } from "react-router-dom";
import { getTemplateDetail, submitNumberCounter, getNumberCounterDetail, updateNumberCounter, deleteNumberCounter } from "../../../utils/numberCounterApi";
import ColorPicker from "../../../components/ColorPicker";

const ContentTab = ({ isTemplateId,updateNumberCounterTabData,numberCounterTabData,templateType,setRefreshWidgetList }) => {
    const [activeSettingElementsTab, setActiveSettingElementsTab] = useState("main");
    const [apiNumberCounter, setApiNumberCounter] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [openDropdown, setOpenDropdown] = useState(null);
    const dropdownRefs = useRef([]);
    const [isSubmitNumberCounter, setIsSubmitNumberCounter] = useState(false); 
    const location = useLocation();
    const isEditPage = location.pathname.startsWith("/customer/edit/number-counter/");
    const { uuid } = useParams();
    const [templateId, setTemplateId] = useState(() => (isEditPage && uuid ? uuid : isTemplateId || ""));
    const [apiSingleNumberCounter, setApiSingleNumberCounter] = useState(null);
    const [numberCounterId, setNumberCounterId] = useState(null);
    const [widgetTitle, setWidgetTitle] = useState(numberCounterTabData.widget_title);
    const [activeCounterValueSource, setActiveCounterValueSource] = useState("Manual");
    const [customColor, setCustomColor] = useState("#000");
    const [endValue, setEndValue] = useState(100000);
    const [startValue, setStartValue] = useState(0);

    //Handlers Custom Color Change
    const handleCustomColorChange = (color) => { 
        setCustomColor(color.hex);
    };

    // Handlers widget Title Change
    const handleWidgetTitleChange = (value) => {
        setWidgetTitle(value);
        updateNumberCounterTabData("widget_title", value);
    };

    //Wrap fetchTemplateDetails in useCallback to prevent re-creation on every render
    const fetchTemplateDetails = useCallback(async () => {
        if (!templateId) return;

        setIsLoading(true);
        try {
            const templateResult = await getTemplateDetail(templateId);
            if (templateResult?.status === true && Array.isArray(templateResult?.data?.data_list)) {
                if(templateType === "White_Theme_Temp"){
                    const firstDataList = templateResult.data.data_list; 
                    if (firstDataList) {
                        setApiNumberCounter(firstDataList); 
                    } else {
                        setApiNumberCounter([]);
                    }
                } else {
                    if (templateResult?.status === true && templateResult?.data?.data_list) {
                        setApiNumberCounter(templateResult.data.data_list);
                    } else {
                        setApiNumberCounter([]);
                    }
                }
            } else {
                setApiNumberCounter([]);
            }
        } catch (error) {
            //console.error("Error fetching data:", error);
        } finally {
            setIsLoading(false);
        }
    }, [templateId,templateType]);

    // Fix useEffect by including fetchTemplateDetails
    useEffect(() => {
        fetchTemplateDetails();
    }, [fetchTemplateDetails]);

    // Also include fetchTemplateDetails in this useEffect
    useEffect(() => {
        if (isEditPage && uuid) {
            setTemplateId(uuid);
        } else {
            setTemplateId(isTemplateId || "");
        }

        fetchTemplateDetails();
    }, [isEditPage, uuid, isTemplateId, fetchTemplateDetails]);

    // Toggle dropdown visibility
    const toggleDropdown = (index) => {
        setOpenDropdown((prev) => (prev === index ? null : index));
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                openDropdown !== null &&
                dropdownRefs.current[openDropdown] &&
                !dropdownRefs.current[openDropdown].contains(event.target) &&
                !event.target.closest(".inner-dropdown-menu")
            ) {
                setOpenDropdown(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [openDropdown]);

    //Submit Number Counter Handler
    const handleSubmitNumberCounter = async (e) => {
        e.preventDefault();
    
        try {
            setIsSubmitNumberCounter(true);
    
            const formData = new FormData();
            formData.append("template_id", templateId);
            formData.append("counter_value_source", activeCounterValueSource);
            formData.append("end_value", e.target.end_value?.value || '');
            formData.append("start_value", e.target.start_value?.value || '');
            formData.append("external_link", e.target.external_link?.value || '');
            formData.append("prefix", e.target.prefix?.value || '');
            formData.append("postfix", e.target.postfix?.value || '');
            formData.append("caption", e.target.caption?.value || '');
            formData.append("image", e.target.image?.files[0] || '');
            formData.append("link", e.target.link?.value || '');
            formData.append("custom_color", customColor);
            formData.append("type", "Dynamic");
    
            //Submit API
            const response = await submitNumberCounter(formData);
            if (response.status) { 
                toast.success(response.message);
                await fetchTemplateDetails();
                setActiveSettingElementsTab("main");

                setRefreshWidgetList(prev => !prev);
            } else {
                toast.error(response.message);
            }
        } catch (error) {
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsSubmitNumberCounter(false);
        }
    };    

    // Handle Edit Click 
    const handleEditNumberCounter = (data) => {
        setOpenDropdown(null);
        setNumberCounterId(data.id);
        setActiveSettingElementsTab("edit_number_counter");
        fetchNumberCounterDetails(data.id);
    };

    // Handle Delete Click
    const handleDeleteNumberCounter = async (data) => {
        setOpenDropdown(null);

        try {
            // Prepare the payload
            const payload = {
                number_counter_id: data.id,
            };

            // API Call
            const api_response = await deleteNumberCounter(payload);
            // Check API Response
            if (api_response.status === true) {
                toast.success(api_response.message);

                // Refresh testimonials
                await fetchTemplateDetails();
                setRefreshWidgetList(prev => !prev);
            } else {
                toast.error(api_response.message);
            }
        } catch (error) {
            toast.error(`${error.message}`);
        }
    };

    // Wrap fetchNumberCounterDetails in useCallback to prevent re-creation on every render
    const fetchNumberCounterDetails = useCallback(async (numberCounterId) => {
        if (!numberCounterId) return;

        setIsLoading(true); 
        try {
            const templateResult = await getNumberCounterDetail(numberCounterId); 
            if (templateResult?.status === true && templateResult?.data) {
                setApiSingleNumberCounter(templateResult.data);
                setCustomColor(templateResult.data.custom_color); 
                setActiveCounterValueSource(templateResult.data.counter_value_source); 
            } else { 
                setApiSingleNumberCounter(null);
            }
        } catch (error) {
            //console.error("Error fetching number counter:", error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    //Update Number Counter Handler
    const handleUpdateNumberCounter = async (e) => {
        e.preventDefault();

        try {
            setIsSubmitNumberCounter(true);
            setOpenDropdown(null);
            // Create FormData object
            const formData = new FormData();
            formData.append("id", numberCounterId);
            formData.append("counter_value_source", activeCounterValueSource);
            formData.append("end_value", e.target.end_value?.value || '');
            formData.append("start_value", e.target.start_value?.value || '');
            formData.append("external_link", e.target.external_link?.value || '');
            formData.append("prefix", e.target.prefix?.value || '');
            formData.append("postfix", e.target.postfix?.value || '');
            formData.append("caption", e.target.caption?.value || '');
            formData.append("image", e.target.image?.files[0] || '');
            formData.append("link", e.target.link?.value || '');
            formData.append("custom_color", customColor);

            //Api Call
            const response = await updateNumberCounter(formData);

            if (response.status) {
                toast.success(response.message);

                //Refresh Number Counter
                await fetchTemplateDetails();
                setRefreshWidgetList(prev => !prev);

                // Switch back to the main view
                setActiveSettingElementsTab("main");
            } else {
                toast.error(response.message);
            }
        } catch (error) {
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsSubmitNumberCounter(false);
        }
    };

    // Handle Counter Value Source Tab input change
    const handleCounterValueSourceChange = (event) => {
        setActiveCounterValueSource(event.target.value);
        updateNumberCounterTabData("counter_value_source", event.target.value);
    };

    return (
        <div className="testimonials-side-baar-layout-tab">
            {activeSettingElementsTab === "main" && (
                <>
                    <span>Content</span>
                    <div className="side-baar-box-header">
                        <div className="add-testimonal-button">
                            <button onClick={() => setActiveSettingElementsTab("add_number_counter")}>+ Add Counter</button>
                        </div>
                        <div className="testimonal-side-baar-list">
                            {isLoading ? (
                                <div className="inner-loader-container">
                                    <div className="inner-spinner"></div>
                                    <p>Loading, please wait...</p>
                                </div>
                            ) : apiNumberCounter.length > 0 ? (
                                <ul>
                                    {apiNumberCounter.map((data, index) => (
                                        <li key={index} className="testimonial-item">
                                            <button type="button" className="testimonial-item-button" onClick={() => handleEditNumberCounter(data)}>
                                                <div className="list-image-testimonal">
                                                    <img src={data.image} alt={data.image_name} />
                                                </div>
                                                <div className="list-text-testimonal">  
                                                    <span>{data.end_value}</span> 
                                                    <p>{data.caption}</p>
                                                </div>
                                            </button>
                                            <div className="list-button-testimonal" ref={(el) => (dropdownRefs.current[index] = el)}>
                                                <button onClick={() => toggleDropdown(index)}>
                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M3.864 8.333a1.363 1.363 0 1 1 0 2.726 1.363 1.363 0 0 1 0-2.726Zm6.136 0a1.363 1.363 0 1 1 .001 2.726A1.363 1.363 0 0 1 10 8.333Zm6.136 0a1.363 1.363 0 1 1 .001 2.726 1.363 1.363 0 0 1 0-2.726Z"></path>
                                                    </svg>
                                                </button>
                                            </div>
                                            {openDropdown === index && (
                                                <div className="inner-dropdown-menu">
                                                    <div className="button-list">
                                                        <button type="button" onClick={() => handleEditNumberCounter(data)}>Edit</button>
                                                    </div>
                                                    <div className="button-list">
                                                        <button type="button" onClick={() => handleDeleteNumberCounter(data)}> Delete</button>
                                                    </div>
                                                </div>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p>No number counter available.</p>
                            )}
                        </div>
                    </div>
                    <div className="side-baar-box-header">
                        <div className="side-baar-box-header">
                            <div className="box-header-row bubble-text">
                                <span>Widget Title</span>
                                <input name="widget_title" type="text" value={widgetTitle} onChange={(e) => handleWidgetTitleChange(e.target.value)} />
                            </div>
                        </div>
                    </div>
                </>
            )}

            {activeSettingElementsTab === "add_number_counter" && (
                <>
                    <div className="inner-box-layout">
                        <form onSubmit={handleSubmitNumberCounter}>
                            <span>
                                <button className="button-color" type="button" onClick={() => setActiveSettingElementsTab("main")}>
                                    <i className="fa fa-angle-left" aria-hidden="true"></i>Cancel
                                </button>
                                Add Counter
                                <button type="submit" className="button-color button-done" disabled={isSubmitNumberCounter}>
                                    {isSubmitNumberCounter ? "Submitting..." : "Done"}
                                </button>
                            </span>

                            <div className="name-tile-row">Counter Value Source</div>
                            <div className="card h-100 position-tabe">
                                <div className="label-box-flex py-0 px-1">
                                    <label className="card-radio-btn">
                                        <input type="radio" name="counter_value_type" className="card-input-element d-none" value="Manual" onChange={handleCounterValueSourceChange} checked={activeCounterValueSource === "Manual"}/>
                                        <div className="card card-body">
                                            <div className="content_sub mt-0">Manual</div>
                                        </div>
                                    </label>
                                    <label className="card-radio-btn">
                                        <input type="radio" name="counter_value_type" className="card-input-element d-none" value="External_Link" onChange={handleCounterValueSourceChange} checked={activeCounterValueSource === "External_Link"}  />
                                        <div className="card card-body">
                                            <div className="content_sub mt-0">External Link</div>
                                        </div>
                                    </label>
                                </div>
                            </div>

                            {/*Source Value Type*/}
                            {activeCounterValueSource === "Manual" && (
                                <>
                                <div className="side-baar-box-header">
                                    <div className="box-header-row bubble-text">
                                        <span>End Value</span>
                                        <input name="end_value" type="number" value={endValue} onChange={(e) => setEndValue(e.target.value)} placeholder="Enter End Value" min="0" step="1"/>
                                    </div>
                                    <div className="box-header-row bubble-text">
                                        <span>Start Value</span>
                                        <input name="start_value" type="number" value={startValue} onChange={(e) => setStartValue(e.target.value)} placeholder="Enter Start Value"  min="0"  step="1"/>
                                    </div>
                                    <p>Choose whether to manually input the counter value or fetch it from an external URL. <a href="https://dashboard.upgadeify.com/customer/apps/number-counter">See this guide</a> for setup instructions and data format requirements.</p>
                                </div>
                                </>
                            )}

                            {/*Source Value Type*/}
                            {activeCounterValueSource === "External_Link" && (
                                <div className="side-baar-box-header">
                                    <div className="box-header-row bubble-text">
                                        <input name="external_link" placeholder="Enter URL..." type="text" />
                                    </div>
                                </div>
                            )}

                            <div className="side-baar-box-header">
                                <div className="box-header-row bubble-text">
                                    <input name="prefix" placeholder="Prefix..." type="text" />
                                </div>
                                <div className="box-header-row bubble-text">
                                    <input name="postfix" placeholder="Postfix..." type="text" />
                                </div>
                                <div className="box-header-row bubble-text">
                                    <input name="caption" placeholder="Caption..." type="text" />
                                </div>
                            </div>

                            <div className="side-baar-box-header">
                                <div className="box-header-row">
                                    <span>Icon</span>
                                    <input type="file" name="image" accept="image/*" />
                                </div>
                            </div>

                            <div className="side-baar-box-header">
                               <div className="side-baar-box-header">
                                    <div className="box-header-row bubble-text">
                                        <input name="link" placeholder="Link..." type="text" />
                                    </div>
                                </div>
                            </div>
                        </form>
                        <div className="side-baar-box-header">
                            <div className="side-baar-box-header">
                                <div className="box-header-row">
                                    <span>Custom Color</span>
                                    <div className="color-pikr-box">
                                        <ColorPicker color={customColor} setColor={setCustomColor} onChangeComplete={handleCustomColorChange} />
                                    </div>
                                </div>
                                <p>Used for setting the individual colors of the counters. Elements applicable for Custom Colors are defined by the settings on Style tab.</p>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {activeSettingElementsTab === "edit_number_counter" && (
                <div className="inner-box-layout">
                    {isLoading ? (
                        <div className="inner-loader-container">
                            <div className="inner-spinner"></div>
                            <p>Loading, please wait...</p>
                        </div>
                    ) : apiSingleNumberCounter ? (
                        <>
                        <form onSubmit={handleUpdateNumberCounter}>
                            <span>
                                <button className="button-color" type="button" onClick={() => setActiveSettingElementsTab("main")}>
                                    <i className="fa fa-angle-left" aria-hidden="true"></i>Cancel
                                </button>
                                Edit Counter
                                <button type="submit" className="button-color button-done" disabled={isSubmitNumberCounter}>
                                    {isSubmitNumberCounter ? "Updating..." : "Update"}
                                </button>
                            </span>

                            <div className="name-tile-row">Counter Value Source</div>
                            <div className="card h-100 position-tabe">
                                <div className="label-box-flex">
                                    <label className="card-radio-btn">
                                        <input type="radio" name="counter_value_type" className="card-input-element d-none" value="Manual" onChange={handleCounterValueSourceChange} checked={activeCounterValueSource === "Manual"}/>
                                        <div className="card card-body">
                                            <div className="content_sub">Manual</div>
                                        </div>
                                    </label>
                                    <label className="card-radio-btn">
                                        <input type="radio" name="counter_value_type" className="card-input-element d-none" value="External_Link" onChange={handleCounterValueSourceChange} checked={activeCounterValueSource === "External_Link"}  />
                                        <div className="card card-body">
                                            <div className="content_sub">External Link</div>
                                        </div>
                                    </label>
                                </div>
                            </div>

                            {activeCounterValueSource === "Manual" && (
                                <div className="side-baar-box-header">
                                    <div className="box-header-row bubble-text">
                                        <span>End Value</span>
                                        <input
                                            name="end_value"
                                            type="number"
                                            value={apiSingleNumberCounter.end_value || ""}
                                            onChange={(e) =>
                                                setApiSingleNumberCounter({
                                                    ...apiSingleNumberCounter,
                                                    end_value: e.target.value,
                                                })
                                            }
                                            placeholder="Enter End Value"
                                            min="0"
                                            step="1"
                                        />
                                    </div>
                                    <div className="box-header-row bubble-text">
                                        <span>Start Value</span>
                                        <input
                                            name="start_value"
                                            type="number"
                                            value={apiSingleNumberCounter.start_value || ""}
                                            onChange={(e) =>
                                                setApiSingleNumberCounter({
                                                    ...apiSingleNumberCounter,
                                                    start_value: e.target.value,
                                                })
                                            }
                                            placeholder="Enter Start Value"
                                            min="0"
                                            step="1"
                                        />
                                    </div>
                                    <p>
                                        Choose whether to manually input the counter value or fetch it from
                                        an external URL.{" "}
                                        <a href="https://dashboard.upgadeify.com/customer/apps/number-counter">
                                            See this guide
                                        </a>{" "}
                                        for setup instructions and data format requirements.
                                    </p>
                                </div>
                            )}

                            {activeCounterValueSource === "External_Link" && (
                                <div className="side-baar-box-header">
                                    <div className="box-header-row bubble-text">
                                        <input
                                            name="external_link"
                                            value={apiSingleNumberCounter.external_link || ""}
                                            onChange={(e) =>
                                                setApiSingleNumberCounter({
                                                    ...apiSingleNumberCounter,
                                                    external_link: e.target.value,
                                                })
                                            }
                                            placeholder="Enter URL..."
                                            type="text"
                                        />
                                    </div>

                                    {/* Hidden start and end value fields to preserve their values */}
                                    <input type="hidden" name="start_value" value={apiSingleNumberCounter.start_value || ""} />
                                    <input type="hidden" name="end_value" value={apiSingleNumberCounter.end_value || ""} />
                                </div>
                            )}

                            <div className="side-baar-box-header">
                                <div className="box-header-row bubble-text">
                                    <input name="prefix" value={apiSingleNumberCounter.prefix || ""}
                                        onChange={(e) => setApiSingleNumberCounter({ ...apiSingleNumberCounter, prefix: e.target.value })} placeholder="Prefix..." type="text" />
                                </div>
                                <div className="box-header-row bubble-text">
                                    <input name="postfix" value={apiSingleNumberCounter.postfix || ""}
                                        onChange={(e) => setApiSingleNumberCounter({ ...apiSingleNumberCounter, postfix: e.target.value })} placeholder="Postfix..." type="text" />
                                </div>
                                <div className="box-header-row bubble-text">
                                    <input name="caption" value={apiSingleNumberCounter.caption || ""}
                                        onChange={(e) => setApiSingleNumberCounter({ ...apiSingleNumberCounter, caption: e.target.value })} placeholder="Caption..." type="text" />
                                </div>
                            </div>

                            <div className="side-baar-box-header">
                                <div className="box-header-row">
                                    <span>Icon</span>
                                    <input type="file" name="image" accept="image/*" />
                                    {apiSingleNumberCounter.image && (
                                        <img src={apiSingleNumberCounter.image} alt="Uploaded" width="50" height="50" />
                                    )}
                                </div>
                            </div>

                            <div className="side-baar-box-header">
                               <div className="side-baar-box-header">
                                    <div className="box-header-row bubble-text">
                                        <input name="link" value={apiSingleNumberCounter.link || ""} onChange={(e) => setApiSingleNumberCounter({ ...apiSingleNumberCounter, link: e.target.value })} placeholder="Link..." type="text" />
                                    </div>
                                </div>
                            </div>
                        </form>
                        <div className="side-baar-box-header">
                            <div className="side-baar-box-header">
                                <div className="box-header-row">
                                    <span>Custom Color</span>
                                    <div className="color-pikr-box">
                                        <ColorPicker color={customColor} setColor={setCustomColor} onChangeComplete={handleCustomColorChange} />
                                    </div>
                                </div> 
                                <p>Used for setting the individual colors of the counters. Elements applicable for Custom Colors are defined by the settings on Style tab.</p>
                            </div>
                        </div>
                        </>
                    ) : (
                        <p>No number counter details. Please try again.</p>
                    )}
                </div>
            )}
        </div>
    );
};

export default ContentTab;